import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, Users } from "lucide-react";
import type { ScannerEvent } from "../qrScanner.api";

interface ScannerEventLayoutProps {
  basePath?: string;
}

type TabValue = "camera" | "attendees";

const ScannerEventLayout = ({ basePath = "/qr-scanner" }: ScannerEventLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<ScannerEvent | null>(null);
  const activeTab: TabValue = location.pathname.includes("/attendees") ? "attendees" : "camera";

  useEffect(() => {
    if (!id) return;
    const eventPath = `${basePath}/event/${id}`;
    if (location.pathname === eventPath) {
      navigate(`${eventPath}/camera`, { replace: true });
    } else if (
      !location.pathname.endsWith("/camera") &&
      !location.pathname.endsWith("/attendees")
    ) {
      navigate(`${eventPath}/camera`, { replace: true });
    }
  }, [basePath, id, location.pathname, navigate]);

  useEffect(() => {
    const storedEvent = sessionStorage.getItem("selectedEvent");
    if (!storedEvent) return;
    try {
      setEvent(JSON.parse(storedEvent) as ScannerEvent);
    } catch {
      setEvent(null);
    }
  }, []);

  const handleTabChange = (tab: TabValue) => navigate(`${basePath}/event/${id}/${tab}`);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      <div className="px-6 pt-4">
        <button
          onClick={() => navigate(`${basePath}/welcome`)}
          className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft
            size={24}
            className="text-gray-700 dark:text-gray-300"
          />
        </button>
        <div className="mt-4">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">
            {event?.title || "Event"}
          </h1>
          {event?.location && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{event.location}</p>
          )}
        </div>
      </div>
      <div className="flex mt-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => handleTabChange("camera")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${activeTab === "camera" ? "text-gray-900 dark:text-white border-b-[3px] border-[#00C49A] dark:border-emerald-400" : "text-gray-500 dark:text-gray-400"}`}
        >
          <Camera size={16} />
          <span>QR Scanner</span>
        </button>
        <button
          onClick={() => handleTabChange("attendees")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${activeTab === "attendees" ? "text-gray-900 dark:text-white border-b-[3px] border-[#00C49A] dark:border-emerald-400" : "text-gray-500 dark:text-gray-400"}`}
        >
          <Users size={16} />
          <span>Scanned Attendees</span>
        </button>
      </div>
      <div className="flex-1 flex flex-col">
        <Outlet />
      </div>
    </div>
  );
};

export default ScannerEventLayout;
